/**
 * L'état d'interface de « Compléter mon profil » : l'étape, les réponses modifiées, la semaine en
 * brouillon, les erreurs et la boîte ouverte. Pur, testé.
 *
 * Port de `CompleteTutorProfileStepper.js` et de `TutorAvailabilityStep.js` :
 *  - les réponses modifiées (`edits`) recouvrent celles du compte : le view-model les fusionne ;
 *  - la semaine en brouillon (`openSlots`, `startingDate`) : `null` suit la semaine servie, comme
 *    « Ma dispo » ; une semaine qui arrive (`RESEED`) l'emporte sur le brouillon, comme l'ancienne étape
 *    (« a newer week wins ») ; la date se choisit sans boîte, elle part avec le profil ;
 *  - toucher une heure avec live ouvre « Créneau réservé à un live », sans « Je ne peux plus assurer ce
 *    créneau » (l'ancienne étape : une demande d'absence) ; une heure libérée est inerte ; pas
 *    d'accusé à la première heure posée, l'engagement se lit avant « Suivant » ;
 *  - une réponse modifiée efface l'erreur de son champ.
 */
import { pressTutorHour } from "../../../../adapters/tutor/shared/tutorWeekAdapter"
import { type UITutorHour } from "../../../types/tutorDispoOffres"
import { type UITutorProfileDraft } from "../../../types/tutorCompte"
// Par son fichier, pas par `design-system/tutor` : le barrel charge les pages, que Jest ne sait pas styler.
import { TUTOR_PROFILE_STEPS } from "../../../../../../design-system/pages/TutorCompleteProfilePage/tutorProfileSteps"
import type {
    TutorProfileField,
    TutorProfileStep,
} from "../../../../../../design-system/pages/TutorCompleteProfilePage"

export type TutorCompleteProfileDialog = { kind: "continuity" } | { kind: "live"; liveTimesLabel?: string }

export type TutorCompleteProfileUiState = {
    step: TutorProfileStep | "done"
    edits: Partial<UITutorProfileDraft>
    errors: Partial<Record<TutorProfileField, string>>
    openSlots: string[] | null
    startingDate: string | null
    dialog: TutorCompleteProfileDialog | null
    submitError: string | null
}

export type TutorCompleteProfileUiAction =
    | { type: "CHANGE"; patch: Partial<UITutorProfileDraft> }
    | { type: "GO_TO"; step: TutorProfileStep }
    | { type: "INVALID"; errors: Partial<Record<TutorProfileField, string>> }
    | { type: "PRESS_HOUR"; hour: UITutorHour; serverOpenSlots: string[] }
    | { type: "PICK_STARTING_DATE"; date: string }
    | { type: "OPEN_CONTINUITY" }
    | { type: "CLOSE_DIALOG" }
    | { type: "RESEED" }
    | { type: "SUBMITTING" }
    | { type: "SUBMIT_FAILED"; message: string }
    | { type: "DONE" }

export const initialTutorCompleteProfileUiState: TutorCompleteProfileUiState = {
    step: "fixedMissions",
    edits: {},
    errors: {},
    openSlots: null,
    startingDate: null,
    dialog: null,
    submitError: null,
}

/** L'étape d'à côté ; `null` au-delà des bords. */
export function neighbourStep(step: TutorProfileStep, offset: 1 | -1): TutorProfileStep | null {
    return TUTOR_PROFILE_STEPS[TUTOR_PROFILE_STEPS.indexOf(step) + offset] ?? null
}

function withoutErrors(
    errors: TutorCompleteProfileUiState["errors"],
    fields: string[]
): TutorCompleteProfileUiState["errors"] {
    if (!fields.some((field) => field in errors)) return errors
    const next = { ...errors }
    for (const field of fields) delete next[field as TutorProfileField]
    return next
}

export function tutorCompleteProfileUiReducer(
    state: TutorCompleteProfileUiState,
    action: TutorCompleteProfileUiAction
): TutorCompleteProfileUiState {
    switch (action.type) {
        case "CHANGE":
            return {
                ...state,
                edits: { ...state.edits, ...action.patch },
                errors: withoutErrors(state.errors, Object.keys(action.patch)),
                submitError: null,
            }
        case "GO_TO":
            return { ...state, step: action.step, errors: {}, dialog: null }
        case "INVALID":
            return { ...state, errors: action.errors }
        case "PRESS_HOUR": {
            const { openSlots, effect } = pressTutorHour(state.openSlots ?? action.serverOpenSlots, action.hour)
            if (effect === "none") return state
            if (effect === "live") {
                return { ...state, dialog: { kind: "live", liveTimesLabel: action.hour.liveTimesLabel } }
            }
            return { ...state, openSlots, errors: withoutErrors(state.errors, ["availability"]) }
        }
        case "PICK_STARTING_DATE":
            return action.date ? { ...state, startingDate: action.date } : state
        case "OPEN_CONTINUITY":
            return { ...state, dialog: { kind: "continuity" } }
        case "CLOSE_DIALOG":
            return state.dialog == null ? state : { ...state, dialog: null }
        case "RESEED":
            return state.openSlots == null && state.startingDate == null
                ? state
                : { ...state, openSlots: null, startingDate: null }
        case "SUBMITTING":
            return { ...state, submitError: null }
        case "SUBMIT_FAILED":
            return { ...state, submitError: action.message }
        case "DONE":
            return { ...state, step: "done", dialog: null, submitError: null }
        default:
            return state
    }
}
